import React, { useState, useEffect } from 'react';
import { Sparkles, Plus, Trash2 } from 'lucide-react';

interface Task {
  _id?: string;
  id: string | number;
  title: string;
  description: string;
  priority: string;
  category: string;
  status: string;
  dueDate?: string | null;
  estimatedTime: string;
  estimatedTimeInHours?: number;
  actualHours?: number;
  assignee?: string;
  tags?: string;
  tagsList?: string[];
  sourceProjectId?: string;
  isPromoted?: boolean;
  day?: number;
  userId?: string;
  weekYear?: number;
  weekNumber?: number;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string | null;
  __v?: number;
  completed?: boolean;
  weight: number;
  projectName?: string;
  name?: string;
}

interface IdeaData {
  id: string;
  title: string;
  description: string;
  category: string;
  tags?: string[];
  priority?: string;
  // Additional fields for editing existing projects
  status?: string;
  dueDate?: string;
  tasks?: Task[];
}

interface AddProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (project: any) => void;
  initialData?: IdeaData | null;
}

const statusOptions = ['Planning', 'In Progress', 'Completed', 'On Hold'];
const categoryOptions = ['Learning', 'Work', 'Personal', 'Other'];

const AddProjectModal: React.FC<AddProjectModalProps> = ({ isOpen, onClose, onAdd, initialData }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState(statusOptions[0]);
  const [dueDate, setDueDate] = useState('');
  const [category, setCategory] = useState(categoryOptions[0]);
  const [tasks, setTasks] = useState<Task[]>([
    { id: 1, title: '', description: '', priority: 'Medium', category, status: 'Todo', dueDate: '', estimatedTime: '1h', estimatedTimeInHours: 1, actualHours: 0, assignee: '', tags: '', tagsList: [], sourceProjectId: undefined, isPromoted: false, day: 1, userId: undefined, weekYear: new Date().getFullYear(), weekNumber: new Date().getMonth() + 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), completedAt: null, __v: 0, completed: false, weight: 100 }
  ]);
  const [isGeneratingTasks, setIsGeneratingTasks] = useState(false);

  // Populate form with initial data when modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      setName(initialData.title);
      setDescription(initialData.description);
      setCategory(initialData.category);
      // Always preload dueDate from project card
      setDueDate(initialData.dueDate || '');
      if (initialData.tasks && initialData.tasks.length > 0) {
        setTasks(
          initialData.tasks.map((task, idx) => ({
            ...task,
            title: task.title || task.name || '',
            id: task.id || task._id || idx + 1,
            dueDate: initialData.dueDate || '',
          }))
        );
      } else {
        setTasks([{ id: 1, title: '', description: '', priority: 'Medium', category, status: 'Todo', dueDate: initialData.dueDate || '', estimatedTime: '1h', estimatedTimeInHours: 1, actualHours: 0, assignee: '', tags: '', tagsList: [], sourceProjectId: undefined, isPromoted: false, day: 1, userId: undefined, weekYear: new Date().getFullYear(), weekNumber: new Date().getMonth() + 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), completedAt: null, __v: 0, completed: false, weight: 100 }]);
      }
    } else if (isOpen && !initialData) {
      // Clear form for new project
      setName('');
      setDescription('');
      setStatus(statusOptions[0]);
      setDueDate('');
      setCategory(categoryOptions[0]);
      setTasks([{ id: 1, title: '', description: '', priority: 'Medium', category, status: 'Todo', dueDate: '', estimatedTime: '1h', estimatedTimeInHours: 1, actualHours: 0, assignee: '', tags: '', tagsList: [], sourceProjectId: undefined, isPromoted: false, day: 1, userId: undefined, weekYear: new Date().getFullYear(), weekNumber: new Date().getMonth() + 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), completedAt: null, __v: 0, completed: false, weight: 100 }]);
    }
  }, [isOpen, initialData, category]);

  const handleTaskChange = (idx: number, field: keyof Task, value: any) => {
    setTasks(tasks.map((task, i) =>
      i === idx ? { ...task, [field]: value } : task
    ));
  };

  const addTask = () => {
    setTasks([...tasks, {
      id: Date.now(),
      title: '',
      description: '',
      priority: 'Medium',
      category,
      status: 'Todo',
      dueDate: dueDate, // Use project due date
      estimatedTime: '1h',
      estimatedTimeInHours: 1,
      actualHours: 0,
      assignee: '',
      tags: '',
      tagsList: [],
      sourceProjectId: undefined,
      isPromoted: false,
      day: 1,
      userId: undefined,
      weekYear: new Date().getFullYear(),
      weekNumber: new Date().getMonth() + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      completedAt: null,
      __v: 0,
      completed: false,
      weight: 0
    }]);
  };

  const removeTask = (idx: number) => {
    setTasks(tasks.filter((_, i) => i !== idx));
  };

  const generateTasksWithAI = async () => {
    if (!name || !description) {
      alert('Please fill in project name and description first');
      return;
    }

    setIsGeneratingTasks(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/analyze/project-tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          projectName: name,
          projectDescription: description,
          category: category
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.tasks && result.tasks.length > 0) {
          const generatedTasks = result.tasks.map((task: any, index: number) => ({
            id: Date.now() + index,
            title: task.name || task.title,
            description: '',
            priority: 'Medium',
            category,
            status: 'Todo',
            dueDate: '',
            estimatedTime: '1h',
            estimatedTimeInHours: 1,
            actualHours: 0,
            assignee: '',
            tags: '',
            tagsList: [],
            sourceProjectId: undefined,
            isPromoted: false,
            day: 1,
            userId: undefined,
            weekYear: new Date().getFullYear(),
            weekNumber: new Date().getMonth() + 1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            completedAt: null,
            __v: 0,
            completed: false,
            weight: task.weight || Math.round(100 / result.tasks.length)
          }));
          setTasks(generatedTasks);
        }
      } else {
        console.error('Failed to generate tasks');
        alert('Failed to generate tasks. Please try again or add them manually.');
      }
    } catch (error) {
      console.error('Error generating tasks:', error);
      alert('Error generating tasks. Please try again or add them manually.');
    } finally {
      setIsGeneratingTasks(false);
    }
  };

  // Fix: Ensure weights are numbers and default to 0 if missing
  const totalWeight = tasks.reduce((sum, t) => sum + (typeof t.weight === 'number' ? t.weight : Number(t.weight) || 0), 0);
  // Weight is an optional refinement, not a submission gate — a project
  // shouldn't be blocked just because per-task weights don't add up to 100.
  const canAdd = name && description && dueDate && category && tasks.length > 0 && tasks.every(t => t.title);

  const handleSubmit = () => {
    if (!canAdd) return;
    
    const projectData: any = {
      title: name,
      description,
      status,
      dueDate,
      category,
      tasks: tasks.map(t => ({
        _id: t._id,
        id: t.id,
        title: t.title,
        description: t.description,
        priority: t.priority,
        category: t.category,
        status: t.status,
        dueDate: dueDate,
        estimatedTime: t.estimatedTime,
        estimatedTimeInHours: t.estimatedTimeInHours,
        actualHours: t.actualHours,
        assignee: t.assignee,
        tags: t.tags,
        tagsList: t.tagsList,
        sourceProjectId: t.sourceProjectId,
        isPromoted: t.isPromoted,
        day: t.day,
        userId: t.userId,
        weekYear: t.weekYear,
        weekNumber: t.weekNumber,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
        completedAt: t.completedAt,
        __v: t.__v,
        completed: t.completed,
        weight: t.weight,
        projectName: t.projectName
      }))
    };
    
    // Include sourceIdeaId if this project is being created from an idea
    console.log('DEBUG: initialData =', initialData);
    console.log('DEBUG: initialData?.id =', initialData?.id);
    console.log('DEBUG: initialData?.status =', initialData?.status);
    console.log('DEBUG: condition result =', initialData?.id && !initialData?.status);
    
    if (initialData?.id && !initialData?.status) {
      projectData.sourceIdeaId = initialData.id;
      console.log('DEBUG: Setting sourceIdeaId =', initialData.id);
    }
    
    console.log('DEBUG: Final projectData =', projectData);
    
    onAdd(projectData);
    setName(''); setDescription(''); setStatus(statusOptions[0]); setDueDate(''); setCategory(categoryOptions[0]); setTasks([{ id: 1, title: '', description: '', priority: 'Medium', category, status: 'Todo', dueDate: '', estimatedTime: '1h', estimatedTimeInHours: 1, actualHours: 0, assignee: '', tags: '', tagsList: [], sourceProjectId: undefined, isPromoted: false, day: 1, userId: undefined, weekYear: new Date().getFullYear(), weekNumber: new Date().getMonth() + 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), completedAt: null, __v: 0, completed: false, weight: 100 }]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg p-6 w-full max-w-lg shadow-lg"
        style={{
          maxHeight: 'calc(100vh - 48px)', // 24px padding top and bottom
          marginTop: '24px',
          marginBottom: '24px',
          overflowY: 'auto',
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1">
            <h2 className="text-xl font-bold">
              {initialData?.status ? 'Edit Project' : initialData ? 'Convert Idea to Project' : 'Add New Project'}
            </h2>
            {initialData && (
              <div className="flex items-center gap-2 mt-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {initialData.category}
                </span>
                {initialData.tags?.map((tag, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-3">
          <input className="w-full border rounded p-2" placeholder="Project Name" value={name} onChange={e => setName(e.target.value)} />
          <textarea className="w-full border rounded p-2" placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} />
          <select className="w-full border rounded p-2" value={status} onChange={e => setStatus(e.target.value)}>
            {statusOptions.map(opt => <option key={opt}>{opt}</option>)}
          </select>
          <input className="w-full border rounded p-2" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          {!initialData && (
            <select className="w-full border rounded p-2" value={category} onChange={e => setCategory(e.target.value)}>
              {categoryOptions.map(opt => <option key={opt}>{opt}</option>)}
            </select>
          )}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-sm">
                Tasks <span className="font-normal text-[color:var(--lyra-color-text-muted)]">— weight optional{totalWeight > 0 ? ` (${totalWeight}%)` : ''}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={generateTasksWithAI}
                  disabled={isGeneratingTasks || !name || !description}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded transition-colors"
                  style={{ background: isGeneratingTasks || !name || !description ? 'var(--app-border-strong)' : 'var(--lyra-color-primary)', color: 'var(--app-on-accent)', cursor: isGeneratingTasks || !name || !description ? 'not-allowed' : 'pointer' }}
                  onMouseEnter={e => { if (!isGeneratingTasks && name && description) e.currentTarget.style.background = 'var(--lyra-color-primary)'; }}
                  onMouseLeave={e => { if (!isGeneratingTasks && name && description) e.currentTarget.style.background = 'var(--lyra-color-primary)'; }}
                >
                  {isGeneratingTasks ? (
                    <>
                      <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={12} />
                      AI Generate
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={addTask}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded transition-colors"
                  style={{ background: 'var(--lyra-color-primary)', color: 'var(--app-on-accent)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--app-primary-deep)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'var(--lyra-color-primary)')}
                >
                  <Plus size={12} />
                  Add
                </button>
              </div>
            </div>
            <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {tasks.map((task, idx) => (
                <div key={task.id} className="flex space-x-2 mb-2 items-center">
                  <input className="flex-1 border rounded p-1 text-sm" placeholder="Task Name" value={task.title} onChange={e => handleTaskChange(idx, 'title', e.target.value)} />
                  <input className="w-16 border rounded p-1 text-sm" type="number" min={0} max={100} placeholder="wt %" title="Weight (optional)" value={task.weight || ''} onChange={e => handleTaskChange(idx, 'weight', Number(e.target.value))} />
                  <button
                    type="button"
                    className="text-red-500 hover:text-red-700 p-1"
                    onClick={() => removeTask(idx)}
                    disabled={tasks.length === 1}
                    title="Remove task"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6 space-x-2">
          <button
            className="px-4 py-2 rounded text-sm font-semibold bg-white border border-[color:var(--app-border-strong)] text-[color:var(--app-text-2)] hover:bg-[color:var(--lyra-color-surface)] transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded text-sm font-semibold text-white transition-colors"
            style={{ background: canAdd ? 'var(--lyra-color-primary)' : 'var(--app-text-3)', cursor: canAdd ? 'pointer' : 'not-allowed' }}
            onMouseEnter={e => { if (canAdd) e.currentTarget.style.background = 'var(--app-primary-deep)'; }}
            onMouseLeave={e => { if (canAdd) e.currentTarget.style.background = 'var(--lyra-color-primary)'; }}
            onClick={handleSubmit}
            disabled={!canAdd}
          >
            {initialData?.status ? 'Update Project' : initialData ? 'Create Project' : 'Add Project'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProjectModal;